/**
 * History filters component (T157).
 *
 * Provides filters for trade history:
 * - Date range picker (start/end)
 * - Symbol filter
 * - Side filter (buy/sell/all)
 * - Period quick select (day/week/month)
 */
import { useState } from 'react';
import { Calendar, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { TradeHistoryFilters } from '@/services/history';

interface HistoryFiltersProps {
  filters: TradeHistoryFilters;
  onFiltersChange: (filters: TradeHistoryFilters) => void;
  onPeriodChange?: (period: string) => void;
  currentPeriod?: string;
}

type QuickPeriod = 'day' | 'week' | 'month' | 'year' | 'all';

const quickPeriods: { value: QuickPeriod; label: string }[] = [
  { value: 'day', label: '1D' },
  { value: 'week', label: '1W' },
  { value: 'month', label: '1M' },
  { value: 'year', label: '1Y' },
  { value: 'all', label: 'All' },
];

const sideOptions = [
  { value: '', label: 'All Sides' },
  { value: 'buy', label: 'Buy' },
  { value: 'sell', label: 'Sell' },
];

export function HistoryFilters({
  filters,
  onFiltersChange,
  onPeriodChange,
  currentPeriod = 'week',
}: HistoryFiltersProps) {
  const [showFilters, setShowFilters] = useState(false);

  const handleSymbolChange = (value: string) => {
    onFiltersChange({
      ...filters,
      symbol: value || undefined,
      page: 1, // Reset to first page on filter change
    });
  };

  const handleSideChange = (value: string) => {
    onFiltersChange({
      ...filters,
      side: value || undefined,
      page: 1,
    });
  };

  const handleStartDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      startDate: value || undefined,
      page: 1,
    });
  };

  const handleEndDateChange = (value: string) => {
    onFiltersChange({
      ...filters,
      endDate: value || undefined,
      page: 1,
    });
  };

  const handlePeriodSelect = (period: QuickPeriod) => {
    // Clear date filters when selecting a quick period
    onFiltersChange({
      ...filters,
      startDate: undefined,
      endDate: undefined,
      page: 1,
    });
    onPeriodChange?.(period);
  };

  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      pageSize: filters.pageSize,
    });
    onPeriodChange?.('week');
  };

  const hasActiveFilters = filters.symbol || filters.side || filters.startDate || filters.endDate;

  return (
    <div className="space-y-4">
      {/* Quick period selection and filter toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        {/* Quick period buttons */}
        <div className="flex rounded-lg border p-1">
          {quickPeriods.map((period) => (
            <button
              key={period.value}
              onClick={() => handlePeriodSelect(period.value)}
              className={cn(
                'px-3 py-1 text-sm rounded-md transition-colors',
                currentPeriod === period.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {period.label}
            </button>
          ))}
        </div>

        {/* Filter toggle button */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={cn(hasActiveFilters && 'border-primary')}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                !
              </span>
            )}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              <X className="mr-1 h-4 w-4" />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Symbol filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Symbol</label>
              <Input
                placeholder="e.g. AAPL"
                value={filters.symbol || ''}
                onChange={(e) => handleSymbolChange(e.target.value.toUpperCase())}
              />
            </div>

            {/* Side filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Side</label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={filters.side || ''}
                onChange={(e) => handleSideChange(e.target.value)}
              >
                {sideOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Start date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">Start Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  className="pl-10"
                  value={filters.startDate || ''}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                />
              </div>
            </div>

            {/* End date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground">End Date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="date"
                  className="pl-10"
                  value={filters.endDate || ''}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryFilters;
